"use client";

import type { WebchatStore } from "./store";

declare global {
  interface Window {
    dpBridgeD?: Bridge;
  }
}

export class Bridge {
  private listeners: Record<string, (eventData: WebChatMessageData) => void> =
    {};

  connected = false;
  origin: string;

  constructor(readonly store: WebchatStore) {
    const url = new URL(window.location.href);
    this.origin = url.searchParams.get("origin") || "";
    window.dpBridgeD = this;
  }

  register() {
    if (!this.origin) {
      return;
    }
    window.addEventListener("message", this.messageHandler.bind(this));
    this.syn();
    this.on("synack", (data) => {
      if (data.event !== "synack") {
        return;
      }
      this.connected = true;
      console.debug("Webchat bridge connected");
      console.debug("Style override:", data.styleOverride);
      this.store.setStyleOverride(data.styleOverride ?? "");
    });
  }

  unregister() {
    if (!this.origin) {
      return;
    }
    window.removeEventListener("message", this.messageHandler.bind(this));
    this.off("synack");
  }

  postMessage(data: WebChatMessageData) {
    if (!this.origin) {
      return;
    }
    window.parent.postMessage(data, this.origin);
  }

  on(eventType: string, listener: (eventData: WebChatMessageData) => void) {
    this.listeners[eventType] = listener;
  }

  off(eventType: string) {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete this.listeners[eventType];
  }

  syn() {
    this.connected = false;
    this.postMessage({ event: "synack" });
  }

  toggle(visible: boolean, expand: boolean) {
    let typ: WebChatToggleEventData["type"] = "normal";
    if (!visible) {
      typ = "minimized";
    } else if (expand) {
      typ = "expanded";
    }
    this.postMessage({ event: "toggle", type: typ });
  }

  private messageHandler(event: MessageEvent<WebChatMessageData>) {
    if (event.origin !== this.origin) {
      return;
    }
    this.listeners[event.data.event]?.(event.data);
  }
}

export interface StyleOptions {
  width: string;
  height: string;
}

export interface WebChatSynAckEventData {
  event: "synack";
  styleOverride?: string;
}

export interface WebChatToggleEventData {
  event: "toggle";
  type: "minimized" | "expanded" | "normal";
  style?: StyleOptions;
}

export type WebChatMessageData =
  | WebChatToggleEventData
  | WebChatSynAckEventData;
